import { DataTable, DataTableColumnDef } from "@shared/EDS/components/DataTable/DataTable";
import {
  PlanTrainUnitDetails,
  TransitionType,
} from "@opstel/query/models/planTrainUnitDetails/planTrainUnitDetails";
import { ClaimsCell } from "./ClaimsCell";
import { TransitionIcon } from "./TransitionIcon";
import {
  SERVICE_TASK_TYPE,
  taskTypeTranslations,
} from "@opstel/query/models/planTrainUnitDetails/serviceTask";
import { ServiceTaskCell, TogglePlannedStateFn } from "./ServiceTaskCell";
import { DATETIME_FORMAT_NO_DATE } from "@shared/common/utils/luxonHelpers";
import { RadioPanel, RadioPanelItem } from "@shared/EDS/components/RadioPanel/RadioPanel";
import { Toolbar } from "@shared/EDS/components/Toolbar";
import { useMemo, useState } from "react";

export interface ServiceTasksOverviewProps {
  readonly planTrainUnitDetails: PlanTrainUnitDetails[];
  readonly isLoading?: boolean;
  readonly toggleServiceTask: TogglePlannedStateFn;
}

function buildColumns(
  toggleServiceTask: TogglePlannedStateFn,
): DataTableColumnDef<PlanTrainUnitDetails>[] {
  return [
    {
      id: "disappearingTrainUnitName",
      header: "Treinnummer",
      accessorKey: "disappearingTrainUnitName",
    },
    {
      id: "trainUnitKind",
      header: "Materieel",
      accessorKey: "trainUnitKind",
    },
    {
      id: "transition",
      header: "Overgang",
      accessorKey: "transition",
      cell: ({ row }) => <TransitionIcon transition={row.original.transition} />,
    },
    {
      id: "transitionDuration",
      header: "Overstand (u)",
      accessorKey: "transitionDuration",
      cell: ({ row }) => row.original.transitionDuration.toFormat("hh:mm"),
    },
    {
      id: "disappearingTime",
      header: "Vertrektijd",
      accessorKey: "disappearingTime",
      cell: ({ row }) => row.original.disappearingTime?.toFormat(DATETIME_FORMAT_NO_DATE),
    },
    ...SERVICE_TASK_TYPE.map((taskType): DataTableColumnDef<PlanTrainUnitDetails> => ({
      id: taskType,
      header: taskTypeTranslations[taskType],
      accessorKey: taskType,
      cell: ({ row }) => {
        const task = row.original.serviceTasks.find((task) => task.type === taskType);
        if (!task) return null;
        return (
          <ServiceTaskCell
            serviceTask={task}
            ptuGuid={row.original.ptuGuid}
            toggleServiceTask={toggleServiceTask}
          />
        );
      },
    })),
    {
      id: "claims",
      header: "Claims",
      accessorKey: "claims",
      cell: ({ row }) => <ClaimsCell claims={row.original.claims} />,
      sortFn: (a, b) => {
        const aClaims = a.original.claims.length;
        const bClaims = b.original.claims.length;
        return aClaims - bClaims;
      },
    },
  ];
}

type TransitionFilterType = TransitionType | "ALL";

export function ServiceTasksOverview({
  planTrainUnitDetails,
  isLoading = false,
  toggleServiceTask,
}: ServiceTasksOverviewProps) {
  const [selectedFilter, setSelectedFilter] = useState<TransitionFilterType>("ALL");
  const planTrainUnitDetailsFiltered = useMemo(() => {
    if (selectedFilter === "ALL") {
      return planTrainUnitDetails;
    }
    return planTrainUnitDetails.filter((task) => task.transition === selectedFilter);
  }, [planTrainUnitDetails, selectedFilter]);

  const filterOptions: RadioPanelItem<TransitionFilterType>[] = [
    { label: "Alles", value: "ALL" },
    { label: "Nacht", value: "NIGHT" },
    { label: "Dag", value: "DAY" },
    { label: "Overig", value: "NIGHT_OUTSIDE_SCENARIO" },
  ];

  return (
    <>
      <Toolbar>
        <div className="mb-m flex w-full items-center justify-between">
          <RadioPanel items={filterOptions} onChange={setSelectedFilter} defaultValue="ALL" />
        </div>
      </Toolbar>
      <DataTable
        data-testid="service-tasks-overview-table"
        className="service-tasks-overview-table"
        columns={buildColumns(toggleServiceTask)}
        data={planTrainUnitDetailsFiltered}
        defaultSort={[{ id: "disappearingTrainUnitName", desc: false }]}
        options={{
          isRowInteractive: () => false,
          emptyMessage: "Geen servicetaken beschikbaar.",
          loadingMessage: "Servicetaken worden geladen...",
        }}
        isLoading={isLoading}
      />
    </>
  );
}
